import { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import { UserContext } from "../../../hooks/UserContext";
import { API } from "../../../api";
import type { Company, Grower, Job } from "../../../api/types";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { INVALID_USERCONTEXT } from "../../../utilities/constants";
import { Oops } from "../../../utilities/oops";
import { MarkJobReadyPageProps, MarkJobReadyPageViewModel } from "./MarkJobReadyPage.types";
import { getCompanies, submitJobReadyForm } from "./MarkJobReadyPage.helpers";
import { logger } from "../../../utilities/logger";
import { MarkJobReadyFormInputs } from "../MarkJobReadyForm";

// oxlint-disable-next-line no-unused-vars
export function useMarkJobReadyPageViewModel(props: MarkJobReadyPageProps): MarkJobReadyPageViewModel {
	const [jobs, setJobs] = useState<Job[]>([]);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [growers, setGrowers] = useState<Grower[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [selectedGrowers, setSelectedGrowers] = useState<Grower[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const hasInitialized = useRef(false);

	// Store all selected jobs across grower changes
	const [persistentSelections, setPersistentSelections] = useState<Map<string, Job>>(new Map());

	const userContext = useContext(UserContext);

	logger.entrypoint = "MarkJobReadyPage";

	async function onCompanyChanged(company: Company | null): Promise<void> {
		try {
			setIsLoading(true);
			setJobs([]);
			setGrowers([]);
			setSelectedGrowers([]);
			// Clear all selections when company changes
			setPersistentSelections(new Map());
			setSelectedCompany(company);
			if (company) {
				const growers = await API.Data.Growers.get.withUnreadyJobs(company);
				setGrowers(growers);
			} else {
				setGrowers([]);
			}
		} catch (error: any) {
			Oops.handleError(error, "MarkJobReadyPage.onCompanyChanged");
		} finally {
			setIsLoading(false);
		}
	}

	async function onGrowersChanged(growers: Grower[]): Promise<void> {
		try {
			setIsLoading(true);
			setJobs([]);
			setSelectedGrowers(growers);
			if (growers.length > 0 && selectedCompany) {
				const jobs = await API.Data.Jobs.get.byGrowers(growers, true);
				setJobs(jobs);
			} else {
				setJobs([]);
			}
		} catch (error: any) {
			Oops.handleError(error, "MarkJobReadyPage.onGrowersChanged");
		} finally {
			setIsLoading(false);
		}
	}

	async function markJobsReady({ selectedJobs }: MarkJobReadyFormInputs): Promise<void> {
		// Guard against concurrent submissions
		if (isSubmitting) {
			logger.warning("Submission already in progress, ignoring duplicate request");
			return;
		}

		try {
			setIsSubmitting(true);
			setIsLoading(true);

			// Submit jobs to API
			await submitJobReadyForm(selectedJobs, userContext);

			// Only clear submitted jobs from persistent selections if submission succeeded
			const newMap = new Map(persistentSelections);
			selectedJobs.forEach((job) => {
				if (job && job.id) {
					newMap.delete(job.id);
				}
			});
			setPersistentSelections(newMap);

			// Refresh current grower's jobs (submitted jobs will be filtered out by API)
			// Using onGrowersChanged instead of onCompanyChanged to preserve selections for other growers
			await onGrowersChanged(selectedGrowers);

			toast.success(`Marked ${selectedJobs.length} jobs as ready!`);
		} catch (error: any) {
			Oops.handleError(error, "MarkJobReadyPage.markJobsReady");
		} finally {
			setIsSubmitting(false);
			setIsLoading(false);
		}
	}

	useEffect(() => {
		// Only run once on mount when userContext is available
		if (hasInitialized.current) {
			return;
		}

		// React advises to declare the async function directly inside useEffect
		async function getData() {
			try {
				setIsLoading(true);
				if (userContext == undefined || !userContext.id) {
					Sentry.captureMessage("userContext is undefined: MarkJobReadyPage.tsx", "info");
					toast.error(INVALID_USERCONTEXT);
					return;
				}
				const companyRecords = await getCompanies(userContext.id);
				setCompanies(companyRecords);
				if (companyRecords.length === 1) {
					const companyRecord = companyRecords[0];
					await onCompanyChanged(companyRecord);
				}
				hasInitialized.current = true;
			} catch (error) {
				Oops.handleError(error, "MarkJobReadyPage.getData");
			} finally {
				setIsLoading(false);
			}
		}

		if (userContext?.id) {
			getData();
		}
	}, [userContext]);

	function isReadyToShowForm(): boolean {
		return !!selectedCompany && (growers.length === 0 || selectedGrowers.length > 0);
	}

	function hasJobs(): boolean {
		return !!selectedCompany && selectedGrowers.length > 0 && jobs.length > 0;
	}

	// Memoized computation of initial selected jobs
	// Merges current API data with persisted user selections (priority, notes)
	const initialSelectedJobs = useMemo(() => {
		const selectedGrowerIds = new Set(selectedGrowers.map((g) => g.id));

		return jobs
			.filter((job) => {
				// Only include jobs that are in persistent selections
				if (!persistentSelections.has(job.id)) return false;

				// Validate job belongs to currently selected growers
				// Job has growerIds array, so check if any match selected growers
				return job.growerIds.some((growerId) => selectedGrowerIds.has(growerId));
			})
			.map((job) => {
				const persistedJob = persistentSelections.get(job.id)!;
				return {
					...job, // Use current job data from API (source of truth)
					fieldPriority: persistedJob.fieldPriority, // Restore user's priority
					submissionNotes: persistedJob.submissionNotes, // Restore user's notes
				};
			});
	}, [jobs, persistentSelections, selectedGrowers]);

	// Callback to update persistent selections when form changes
	const onSelectionChange = useCallback((selectedJobs: Job[]) => {
		// Update the persistent selections map
		const newMap = new Map<string, Job>();
		selectedJobs.forEach((job) => {
			if (job && job.id) {
				newMap.set(job.id, job);
			}
		});
		setPersistentSelections(newMap);
	}, []);

	return {
		jobs,
		companies,
		growers,
		selectedCompany,
		selectedGrowers,
		isLoading,
		onCompanyChanged,
		onGrowersChanged,
		markJobsReady,
		onSelectionChange,
		isReadyToShowForm,
		hasJobs,
		initialSelectedJobs,
	};
}
